import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { UnsubscribeService } from '@app/infrastructure/unsubscribe/unsubscribe.service';
import { ISavingsGraphPointsDTO } from '@modules/potential-save-modal/dto/i-potential-savings.dto';
import {
	savingsGraphAxisLabel,
	savingsGraphAxisLine,
	savingsGraphConfig,
} from '@modules/potential-save-modal/savings-graph/savings-graph.config';
import { EChartsOption } from 'echarts';
import { takeUntil } from 'rxjs/operators';

interface View {
	value: string;
	isActive: boolean;
}

@Component({
	selector: 'app-savings-graph',
	templateUrl: './savings-graph.component.html',
	styleUrls: ['./savings-graph.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [UnsubscribeService],
})
export class SavingsGraphComponent implements OnInit, OnChanges {
	@Input() public data: ISavingsGraphPointsDTO;
	@Output() public openTotalSavingPanel: EventEmitter<void> = new EventEmitter();
	@Output() public close: EventEmitter<void> = new EventEmitter();

	public readonly workloadShownControl = new FormControl(false);
	public readonly views: View[] = [
		{ value: '36M', isActive: true },
		{ value: '3Y', isActive: false },
	];
	public chartUpdatedOption: EChartsOption = savingsGraphConfig;
	private readonly seriesNames = [
		{ key: 'zestySavings', label: 'Zesty', type: 'bar' },
		{ key: 'selfSavings', label: 'Saving Plans', type: 'bar' },
		{ key: 'zestyFlex', label: 'Zesty tolerance', type: 'line' },
		{ key: 'selfFlex', label: 'Saving Plans tolerance', type: 'line' },
	];

	private get isWorkloadShown(): boolean {
		return this.workloadShownControl.enabled && this.workloadShownControl.value;
	}

	constructor(private unsubscribe$: UnsubscribeService) {}

	public ngOnInit(): void {
		this.trackWorkloadShownControl();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.data?.currentValue) {
			this.handleDataUpdate(changes.data.currentValue);
		}
	}

	public toggleView(selectedView: View): void {
		this.views.forEach((view) => {
			view.isActive = view.value === selectedView.value;
		});
		if (selectedView.value === '3Y') {
			this.workloadShownControl.disable();
			this.generateGraphByYears(this.data);
		} else {
			this.workloadShownControl.enable();
			this.generateGraphByMonths(this.data);
		}
	}

	private trackWorkloadShownControl(): void {
		this.workloadShownControl.valueChanges
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(() => this.generateGraphByMonths(this.data));
	}

	private generateGraphByYears(data: ISavingsGraphPointsDTO): void {
		const xAxisData = [1, 2, 3];
		this.chartUpdatedOption = {
			...savingsGraphConfig,
			xAxis: [
				{
					...savingsGraphConfig.xAxis,
					axisLine: savingsGraphAxisLine,
					axisLabel: savingsGraphAxisLabel,
					...{ data: xAxisData },
				},
			],
			yAxis: { ...savingsGraphConfig.yAxis[0] },
			// @ts-ignore
			series: this.seriesNames
				.filter(({ type }) => type === 'bar')
				.map(({ key, label, type }) => ({
					name: label,
					type,
					data: this.calculateDataByYears(key, data),
				})),
		};
	}

	private calculateDataByYears(key: string, data: ISavingsGraphPointsDTO) {
		const year1 = Object.values(data)
			.slice(0, 12)
			.map((item) => +item[key])
			.reduce((prev, curr) => prev + curr, 0);
		const year2 = Object.values(data)
			.slice(12, 24)
			.map((item) => +item[key])
			.reduce((prev, curr) => prev + curr, 0);
		const year3 = Object.values(data)
			.slice(24)
			.map((item) => +item[key])
			.reduce((prev, curr) => prev + curr, 0);

		return [year1, year2, year3].map((item) => ({
			value: item,
			itemStyle: {
				borderRadius: [8, 8, 0, 0],
			},
		}));
	}

	private generateGraphByMonths(data: ISavingsGraphPointsDTO): void {
		const xAxisData = Object.keys(data).map((item) => `${item}`);
		this.chartUpdatedOption = {
			...savingsGraphConfig,
			xAxis: [
				{
					...savingsGraphConfig.xAxis,
					axisLine: savingsGraphAxisLine,
					axisLabel: savingsGraphAxisLabel,
					...{ data: xAxisData },
				},
			],
			yAxis: this.isWorkloadShown ? savingsGraphConfig.yAxis : { ...savingsGraphConfig.yAxis[0] },
			legend: {
				...savingsGraphConfig.legend,
				data: this.seriesNames.filter(({ type }) => type === 'bar').map((item) => item.label),
			},
			// @ts-ignore
			series: this.seriesNames
				.filter(({ type }) => (this.isWorkloadShown ? true : type === 'bar'))
				.map(({ key, label, type }) => ({
					name: label,
					type,
					...(type === 'line' && {
						yAxisIndex: 1,
						tooltip: {
							valueFormatter: (value) => (+value as any).toFixed(2) + '%',
						},
					}),
					data: this.calculateDataByMonths(key, data, type),
				})),
		};
	}

	private calculateDataByMonths(key: string, data: ISavingsGraphPointsDTO, type?: string) {
		return Object.values(data).map((item) => ({
			value: type === 'bar' ? +item[key] : +item[key] * 100,
			...(type === 'bar' && {
				itemStyle: {
					borderRadius: [20, 20, 0, 0],
				},
			}),
		}));
	}

	private handleDataUpdate(data: ISavingsGraphPointsDTO): void {
		if (!data || !Object.keys(data).length) {
			this.chartUpdatedOption = undefined;
			return;
		}
		this.generateGraphByMonths(data);
	}
}
